import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import PropTypes from "prop-types";
import L from "leaflet"; // Ensure L is imported
import { getBranchPerformance } from "services/api";
import { useTranslation } from "react-i18next";
import { CircularProgress, Box } from "@mui/material";

const getIconByPerformance = (performance) => {
  let iconUrl;

  if (!performance || performance == -1) {
    iconUrl =
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-grey.png"; // ⚫ No Data
  } else if (performance >= 105) {
    iconUrl =
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png"; // 🟢 Excellent Performance
  } else if (performance >= 90 && performance < 105) {
    iconUrl =
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png"; // 🔵 Good Performance
  } else if (performance >= 80 && performance < 90) {
    iconUrl =
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-orange.png"; // 🔵 Good Performance
  } else if (performance >= 70 && performance < 80) {
    iconUrl =
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-yellow.png"; // 🟡 Moderate Performance
  } else {
    iconUrl =
      "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png"; // 🔴 Very Low Performance
  }

  return new L.Icon({
    iconUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
    shadowSize: [41, 41],
  });
};

const getStatusColor = (performance) => {
  if (performance == -1)
    return "grey"; // Excellent
  else if (performance >= 105)
    return "blue"; // Average
  else if (performance >= 90 && performance < 105)
    return "green"; // Average
  else if (performance >= 80 && performance < 90)
    return "orange"; // Average
  else if (performance >= 70 && performance < 80)
    return "yellow"; // Average
  else return "red"; // Poor
};

const BranchPerformanceMap = ({ branches, year, month, center }) => {
  const [branchResults, setBranchResults] = useState({});
  const { t } = useTranslation();
  // useEffect(() => {
  //   setBranchResults({});
  // }, [month, year]);

  useEffect(() => {
    const fetchPerformanceData = async () => {
      // if (branches.length === 0) return;
      if (!branches || branches.length === 0) return; // Ensure branches is defined

      const results = {};
      await Promise.all(
        branches.map(async (branch) => {
          const result = await getBranchPerformance(year, month, branch.branchId);
          results[branch.branchId] = result?.overallBranchAverage?.toFixed(2) ?? "N/A";
        })
      );

      setBranchResults(results);
    };

    fetchPerformanceData();
  }, [month, year, branches]);

  const handleMarkerClick = async (branchId) => {
    if (!branchResults[branchId]) {
      const result = await getBranchPerformance(year, month, branchId);
      setBranchResults((prevResults) => ({
        ...prevResults,
        [branchId]: result.overallBranchAverage.toFixed(2) ?? "N/A",
      }));
    }
  };

  return (
    <MapContainer
      center={center} // Egypt's latitude and longitude
      zoom={6}
      style={{ height: "300px", width: "100%" }}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      />
      {branches?.map((branch, index) => (
        <Marker
          key={index}
          position={[branch.latitude, branch.longitude]}
          // icon={customIcon} eventHandlers={{ click: () => handleMarkerClick(branch.branchId) }}
          icon={getIconByPerformance(branchResults[branch.branchId])}
        >
          <Popup>
            <strong>{branch.branchName}</strong> <br />
            {Number(branchResults[branch.branchId]) === -1 ? (
              t("user.dashboards.noData")
            ) : branchResults[branch.branchId] ? (
              <>
                {branchResults[branch.branchId]}%
                <span
                  style={{
                    display: "inline-block",
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor: getStatusColor(branchResults[branch.branchId]),
                    marginLeft: "5px",
                  }}
                ></span>
              </>
            ) : (
              // t("user.dashboards.noData")
              <Box display="flex" justifyContent="center" p={1}>
                <CircularProgress size={20} sx={{ color: "black" }} />
              </Box>
            )}
          </Popup>
        </Marker>
      ))}
    </MapContainer>
  );
};

BranchPerformanceMap.propTypes = {
  branches: PropTypes.arrayOf(
    PropTypes.shape({
      latitude: PropTypes.number.isRequired,
      longitude: PropTypes.number.isRequired,
      branchId: PropTypes.number.isRequired,
      branchName: PropTypes.string.isRequired,
      address: PropTypes.string.isRequired,
    })
  ).isRequired,
  year: PropTypes.number.isRequired,
  month: PropTypes.number.isRequired,
  center: PropTypes.array.isRequired,
};

export default BranchPerformanceMap;
